"use client";

import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "use-debounce";
import { useParams } from "@/hooks";
import {
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from "@/store/modules/suppliers/thunks";
import { AppDispatch } from "@/store";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { supplierSchema } from "@/validations";
import { fetchExpenses } from "@/store/modules/expense/thunks";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { useAuth } from "@/context/AuthContext";

const useSupplier = (onSuccess?: () => void) => {
  const { params, handlePage, updateParams } = useParams();
  const { hasPermission } = usePermission();
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const { currency, establishment, baseURL } = useAuth();
  const [searchValue, setSearchValue] = useState<string>(params.search || "");

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(supplierSchema),
    defaultValues: {
      name: "",
      phone: "",
      address: "",
      key_type: "",
      pix_key: "",
    },
  });

  const setFormData = (data: Partial<typeof supplierSchema.__outputType>) => {
    reset(data);
  };

  const onSubmit = async (data: typeof supplierSchema.__outputType) => {
    try {
      let result;
      // Sanitização de campos opcionais para não enviar null ao backend
      const sanitized = {
        ...data,
        address: data.address ?? "",
        key_type: data.key_type ?? "",
        pix_key: data.pix_key ?? "",
      } as typeof data;
      const buildFormData = (root: typeof supplierSchema.__outputType) => {
        const fd = new FormData();
        Object.entries(root).forEach(([k, v]) => {
          // Ignora apenas undefined/null. Envia string vazia explicitamente para o backend não interpretar como null.
          if (v === undefined || v === null) return;
          fd.append(k, typeof v === "string" ? v : String(v));
        });
        if (pendingImageFile) fd.append("image", pendingImageFile);
        return fd;
      };

      if (supplierSlug && hasPermission("update supplier")) {
        if (pendingImageFile) {
          const formData = buildFormData(sanitized);
          result = await dispatch(
            updateSupplier({
              slug: supplierSlug,
              data: formData,
            } as any),
          );
        } else {
          result = await dispatch(
            updateSupplier({ slug: supplierSlug, ...sanitized }),
          );
        }
      } else {
        if (hasPermission("create supplier")) {
          if (pendingImageFile) {
            const formData = buildFormData(sanitized);
            result = await dispatch(createSupplier(formData as any));
          } else {
            result = await dispatch(createSupplier(sanitized));
          }
        }
      }

      if (
        createSupplier.fulfilled.match(result) ||
        updateSupplier.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        reset();
        setSupplierSlug(null);
        setPendingImageFile(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const { suppliers, loading, error, fetched } = useSelector(
    (state: any) => state.suppliers,
  );
  const expenses = useSelector((state: any) => state.expenses);

  const dispatch: AppDispatch = useDispatch();

  const [supplierSlug, setSupplierSlug] = useState<any>();
  const [currentPage, setCurrentPage] = useState(suppliers?.current_page ?? 1);
  const isInitialMount = useRef(true);
  const previousSearchRef = useRef<string>(params.search || "");
  const previousParamsStringRef = useRef<string>("");

  // Debounce para busca usando use-debounce
  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  // Atualiza os parâmetros quando o valor debounced muda
  useEffect(() => {
    if (debouncedSearchValue !== previousSearchRef.current) {
      previousSearchRef.current = debouncedSearchValue;
      updateParams({
        search: debouncedSearchValue,
        current_page: 1,
      });
      setCurrentPage(1);
    }
  }, [debouncedSearchValue, updateParams]);

  // Busca suppliers quando params muda (evita loop comparando string serializada)
  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment) return;
    if (!hasPermission("read supplier")) return;

    const currentParamsString = JSON.stringify(params);

    if (isInitialMount.current) {
      // Primeira renderização - busca suppliers e expenses
      dispatch(fetchExpenses(params));
      if (!fetched && !loading) {
        dispatch(fetchSuppliers(params));
      }
      isInitialMount.current = false;
      previousParamsStringRef.current = currentParamsString;
      return;
    }

    // Só busca se params realmente mudou (comparação por string)
    if (currentParamsString !== previousParamsStringRef.current) {
      previousParamsStringRef.current = currentParamsString;
      dispatch(fetchSuppliers(params));
    }
  }, [
    params,
    fetched,
    loading,
    hasPermission,
    dispatch,
    baseURL,
    establishment,
  ]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const totalItems = suppliers?.total ?? 0;
  const itemsPerPage = suppliers?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;
  const pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);

  useEffect(() => {
    if (suppliers?.current_page && suppliers.current_page !== currentPage) {
      setCurrentPage(suppliers.current_page);
    }
  }, [suppliers?.current_page, currentPage]);

  const handleDelete = async () => {
    try {
      if (supplierSlug && hasPermission("delete supplier")) {
        await dispatch(deleteSupplier(supplierSlug));
        setSupplierSlug(null);

        return true;
      }
    } catch (error) {
      console.error("Error deleting supplier:", error);
    }
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    suppliers,
    loading,
    error,
    setSupplierSlug,
    handleDelete,
    setFormData,
    control,
    expenses,
    handlePage,
    params,
    currentPage,
    pagesArray,
    handlePageChange,
    currency,
    handleFilterChange,
    searchValue,
    pendingImageFile,
    setPendingImageFile,
  };
};

export default useSupplier;
